import React from 'react';
import { css } from '@linaria/core';
import { fontStacks, typographyStyles, COLORS } from '@livecommerce/design-system';

const { getBodyText, getMediumHeadline } = typographyStyles;

const { fontFamily, displayFontFamily } = fontStacks;

import { MuiThemeProvider, createTheme } from '@material-ui/core/styles/index.js';

const { assetsCdnUrl } = require('../../../client-config.js');

export const colors = {
  red: {
    main: COLORS.red_500,
    shade: COLORS.red_700,
    contrastText: COLORS.black,
  },
  blue: {
    main: COLORS.blue_500,
    shade: COLORS.blue_800,
    contrastText: COLORS.black,
  },
  purple: {
    main: COLORS.purple_500,
    shade: COLORS.purple_600,
    contrastText: COLORS.black,
  },
  green: {
    main: '#16E7CF',
    shade: '#007C7C',
    contrastText: COLORS.black,
  },
  orange: {
    main: COLORS.yellow_500,
    shade: COLORS.yellow_600,
    contrastText: COLORS.black,
  },
  black: {
    main: COLORS.black,
    shade: COLORS.black,
    contrastText: COLORS.white,
  },
  white: {
    main: COLORS.white,
    shade: COLORS.white,
    contrastText: COLORS.black,
  },
  gray: {
    100: COLORS.black,
    80: COLORS.dark_500,
    60: COLORS.dark_300,
    40: COLORS.light_700,
    20: COLORS.light_400,
    0: COLORS.light_100,
  },
};

export const globals = css`
  :global() {
    @font-face {
      font-family: 'Haas Grot';
      src: url('${assetsCdnUrl}/fonts/neue-haas-grotesk/NeueHaasGrotText-75Bold-Web.woff2')
          format('woff2'),
        url('${assetsCdnUrl}/fonts/neue-haas-grotesk/NeueHaasGrotText-75Bold-Web.woff')
          format('woff');
      font-weight: 700;
      font-style: normal;
      font-display: swap;
    }

    @font-face {
      font-family: 'Haas Grot';
      src: url('${assetsCdnUrl}/fonts/neue-haas-grotesk/NeueHaasGrotText-65Medium-Web.woff2')
          format('woff2'),
        url('${assetsCdnUrl}/fonts/neue-haas-grotesk/NeueHaasGrotText-65Medium-Web.woff')
          format('woff');
      font-weight: 500;
      font-style: normal;
      font-display: swap;
    }

    @font-face {
      font-family: 'Haas Grot';
      src: url('${assetsCdnUrl}/fonts/neue-haas-grotesk/NeueHaasGrotText-55Roman-Web.woff2')
          format('woff2'),
        url('${assetsCdnUrl}/fonts/neue-haas-grotesk/NeueHaasGrotText-55Roman-Web.woff')
          format('woff');
      font-weight: normal;
      font-style: normal;
      font-display: swap;
    }

    @font-face {
      font-family: 'Haas Grot Display';
      src: url('${assetsCdnUrl}/fonts/neue-haas-grotesk/NeueHaasGrotDisp-65Medium-Web.woff2')
          format('woff2'),
        url('${assetsCdnUrl}/fonts/neue-haas-grotesk/NeueHaasGrotDisp-65Medium-Web.woff')
          format('woff');
      font-weight: normal;
      font-style: normal;
      font-display: swap;
    }

    body {
      margin: 0;
      padding: 0;
      font-family: 'Haas Grot', Helvetica, Arial, Verdana, Tahoma, sans-serif !important;
      color: ${COLORS.dark_900};
      ${getBodyText(14, 400)};
      background-color: ${COLORS.dark_700};
    }

    @media print {
      body {
        background-color: ${COLORS.white};
      }
    }

    body::backdrop {
      background-color: ${COLORS.dark_700};
    }

    code {
      font-family: source-code-pro, Menlo, Monaco, Consolas, 'Courier New', monospace;
    }

    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
      font-family: 'Haas Grot Display', Helvetica, Arial, Verdana, Tahoma, sans-serif !important;
    }

    input:focus,
    textarea:focus,
    button:focus {
      outline: none;
    }

    input.has-focus-outline,
    textarea.has-focus-outline,
    button.has-focus-outline {
      outline: 5px auto Highlight;
      outline: 5px auto -webkit-focus-ring-color;
    }

    .fade-enter {
      opacity: 0;
    }

    .fade-enter-active {
      opacity: 1;
      transition: opacity 200ms ease-in;
    }

    .fade-exit {
      opacity: 1;
    }

    .fade-exit-active {
      opacity: 0;
      transition: opacity 200ms ease-in;
    }

    @media only screen and (max-width: 767px) {
      /* For tablets: */
      .mobile-hide {
        display: none;
      }

      .mobile-show {
        display: block;
      }
    }

    @media only screen and (min-width: 768px) {
      /* For desktop: */
      .desktop-hide {
        display: none;
      }

      .desktop-show {
        display: block;
      }
    }

    html {
      box-sizing: border-box;
    }

    *,
    *::before,
    *::after {
      box-sizing: inherit;
    }

    strong,
    b {
      font-weight: 700;
    }
    textarea {
      font-family: ${fontFamily};
    }
  }
`;

const palette = {
  divider: COLORS.light_100,
  background: { default: COLORS.white },
  primary: {
    main: COLORS.blue_500,
    shade: COLORS.blue_800,
    contrastText: COLORS.black,
  },
  secondary: {
    main: COLORS.purple_500,
    shade: COLORS.purple_600,
    contrastText: COLORS.black,
  },
  error: {
    main: COLORS.red_500,
    shade: COLORS.red_700,
    contrastText: COLORS.black,
  },
  info: {
    main: COLORS.dark_500,
    contrastText: COLORS.white,
  },
  success: {
    main: '#16E7CF',
    shade: '#007C7C',
    contrastText: COLORS.black,
  },
  warning: {
    main: COLORS.yellow_500,
    shade: COLORS.yellow_600,
    contrastText: COLORS.black,
  },
  text: {
    primary: COLORS.black,
    secondary: COLORS.dark_300,
    disabled: COLORS.dark_300,
    hint: COLORS.dark_300,
  },
  ...colors,
};

const typography = {
  useNextVariants: true,
  fontFamily,
  h1: getMediumHeadline(48),
  h2: getMediumHeadline(18),
  h3: {
    fontFamily: displayFontFamily,
    fontWeight: 500,
    fontSize: 16,
    lineHeight: '19px',
  },
  body1: getBodyText(14, 400),
};

const sidebar = {
  desktop: {
    width: 254,
    padding: 0,
  },
  mobile: {
    width: 271,
  },
};

const overrides = {
  MuiBackdrop: {
    root: {
      backgroundColor: 'rgba(0, 0, 0, .3)',
    },
  },
};

export const theme = {
  palette,
  typography,
  sidebar,
  overrides,
};

const createdTheme = createTheme({
  palette,
  typography,
  sidebar,
  overrides,
});

const Theme = (props) => {
  const { children } = props;
  return <MuiThemeProvider theme={createdTheme}>{children}</MuiThemeProvider>;
};

export default Theme;
